import React, { useState } from "react";

import {
  BulkImportRequestFragmentFragment,
  useConfirmBulkUserImportMutation,
} from "~/components/Admin/User/BulkImport/BulkImport.generated";
import { PrimaryButton, SecondaryButton } from "~/components/common/buttons";

interface Props {
  orgSlug: string;
  request: BulkImportRequestFragmentFragment;
  onConfirmed: (updatedRequest: BulkImportRequestFragmentFragment) => void;
}

const OUTCOME_BADGE: Record<string, { label: string; bg: string }> = {
  NEW_USER: { label: "New User", bg: "#28a745" },
  EXISTING_USER_NEW_GROUP: { label: "Existing — New Group", bg: "#007bff" },
  NO_OP: { label: "No Change", bg: "#6c757d" },
  DISABLED_USER: { label: "Disabled", bg: "#fd7e14" },
  ERROR: { label: "Error", bg: "#dc3545" },
  UNPROCESSED: { label: "Unprocessed", bg: "#aaa" },
};

const Badge = ({ label, bg }: { label: string; bg: string }) => (
  <span
    style={{
      display: "inline-block",
      background: bg,
      color: "#fff",
      borderRadius: 3,
      padding: "1px 7px",
      fontSize: "0.72rem",
      fontWeight: 600,
      whiteSpace: "nowrap",
      verticalAlign: "middle",
      marginLeft: 6,
    }}
  >
    {label}
  </span>
);

const SUMMARY_COLS = [
  { label: "New Users", key: "newUser" as const },
  { label: "Existing — New Group", key: "existingUserNewGroup" as const },
  { label: "No Change", key: "noOp" as const },
  { label: "Disabled", key: "disabledUser" as const },
  { label: "Errors", key: "error" as const },
];

export const ReviewStep = ({ orgSlug, request, onConfirmed }: Props) => {
  const { rowCounts, rows } = request;
  const exportUrl = `/cleancapital/admin/${orgSlug}/bulk-imports/${request.id}/export.xlsx`;
  const [confirmImport, { loading }] = useConfirmBulkUserImportMutation();
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      const result = await confirmImport({
        variables: { requestId: request.id },
      });
      const payload = result.data?.confirmBulkUserImport;
      if (!payload) return;
      if (payload.errors.length > 0) {
        setError(payload.errors[0]);
        return;
      }
      if (payload.request) {
        onConfirmed(payload.request);
      }
    } catch (e: any) {
      setError(e?.message ?? "An unexpected error occurred.");
    }
  };

  return (
    <div>
      {/* Summary table */}
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          marginBottom: "1.5rem",
          fontSize: "0.875rem",
        }}
      >
        <thead>
          <tr>
            {SUMMARY_COLS.map(({ label }) => (
              <th
                key={label}
                style={{
                  padding: "6px 12px",
                  borderBottom: "2px solid #dee2e6",
                  textAlign: "center",
                  fontWeight: 600,
                  color: "#495057",
                }}
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {SUMMARY_COLS.map(({ label, key }) => (
              <td
                key={label}
                style={{
                  padding: "8px 12px",
                  textAlign: "center",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                  borderBottom: "1px solid #dee2e6",
                }}
              >
                {rowCounts[key]}
              </td>
            ))}
          </tr>
        </tbody>
      </table>

      {/* Row preview table */}
      <div style={{ overflowX: "auto" }}>
        <table
          className="table table-sm table-bordered"
          style={{ fontSize: "0.875rem" }}
        >
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Name</th>
              <th>Email</th>
              <th>Group</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const badge =
                OUTCOME_BADGE[row.outcome] ?? OUTCOME_BADGE.UNPROCESSED;
              return (
                <tr key={row.id}>
                  <td style={{ color: "#888" }}>{row.rowNumber + 1}</td>
                  <td>
                    {row.firstName} {row.lastName}
                  </td>
                  <td>
                    {row.email}
                    <Badge label={badge.label} bg={badge.bg} />
                    {row.outcomeDetail && (
                      <div
                        style={{
                          fontSize: "0.72rem",
                          color: "#888",
                          marginTop: 2,
                        }}
                      >
                        {row.outcomeDetail}
                      </div>
                    )}
                  </td>
                  <td>
                    {row.userGroupName ?? "—"}
                    {row.groupIsNew && <Badge label="New" bg="#17a2b8" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {error && (
        <p
          style={{ color: "#dc3545", fontSize: "0.875rem", margin: "0.5rem 0" }}
        >
          {error}
        </p>
      )}

      <div className="d-flex justify-content-between mt-3">
        <a href={exportUrl} download>
          <SecondaryButton as="span">Download plan (.xlsx)</SecondaryButton>
        </a>
        <PrimaryButton onClick={handleConfirm} disabled={loading}>
          {loading ? "Importing…" : "Confirm & Import →"}
        </PrimaryButton>
      </div>
    </div>
  );
};
